import ContactSelect from '@/components/Form/Shared/ContactSelect'
import { Badge } from '@/components/ui/badge'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getContactById, updateContact } from '@/modules/Customer/services'
import { CustomerContact } from '@/modules/Customer/types'
import { Users, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import { Typogaphy, Button } from 'vnetwork-ui'
import GreyMarket from '../GreyMarket'
import { schemaUpdateContact } from './schema'
import { yupResolver } from '@hookform/resolvers/yup'
import { useModal } from '@/contexts/modal'
import ViewEditContact from './ViewEditContact'
import { useCustomerReadOnly } from '../../CustomerReadOnlyContext'

interface ContactForm {
    contact_id: number
}

interface ContactProps {
    showInfoContact?: boolean
}

const Contact: React.FC<ContactProps> = ({ showInfoContact = false }) => {
    const { id } = useParams()
    const { t } = useTranslation()
    const { setModal } = useModal()
    const [searchParams, setSearchParams] = useSearchParams()
    const [totalContacts, setTotalContacts] = useState(0)
    const [isEditing, setIsEditing] = useState(false)
    const isReadOnly = useCustomerReadOnly()

    const { control, watch, setValue } = useForm<ContactForm>({
        defaultValues: {
            contact_id: Number(searchParams.get('contact_id')) || 0,
        },
        resolver: yupResolver(schemaUpdateContact) as any,
    })

    const editFormControl = useForm<CustomerContact>({
        resolver: yupResolver(schemaUpdateContact) as any,
    })

    const { data, renderState } = useQueryWithHandler(
        {
            queryFn: () =>
                getContactById({
                    customer_id: Number(id),
                    id: Number(watch('contact_id')),
                }),
            queryKey: [QUERY_KEYS.CONTACTS, id, watch('contact_id')],
            enabled: !!watch('contact_id'),
        },
        {
            showLoading: false,
            showError: true,
        }
    )

    const handleEdit = async (values: CustomerContact) => {
        const contact_payload: Partial<CustomerContact> = {
            contact_name: values.contact_name,
            contact_email: values.contact_email,
            contact_phone: values.contact_phone,
            contact_position: values.contact_position,
            contact_industry: values.contact_industry,
            contact_source: values.contact_source,
            contact_note: values.contact_note,
            contact_service_interestings: values.contact_service_interestings,
            contact_cs_followers: values.contact_cs_followers,
            contact_sale_followers: values.contact_sale_followers,
            contact_channel: values?.contact_channel,
            contact_address: values.contact_address,
            contact_language: values.contact_language || 'vi',
            contact_roles: values.contact_roles?.length
                ? values.contact_roles
                : ['customer'],
        }

        // Affiliate link is tri-state: only send affiliate_id when the user actually
        // touched the picker, so editing other contact fields never clobbers the
        // customer's existing referrer link. Cleared picker ('' / undefined) => null.
        if (editFormControl.formState.dirtyFields.affiliate_id) {
            const picked = values.affiliate_id
            contact_payload.affiliate_id =
                picked === '' || picked === undefined || picked === null
                    ? null
                    : Number(picked)
        }

        const result = await updateContact({
            contact_id: watch('contact_id'),
            customer_id: Number(id),
            contact_payload,
        })
        setIsEditing(false)
        return result
    }

    // Reset form khi data thay đổi
    useEffect(() => {
        if (data) {
            editFormControl.reset(data)
        }
    }, [data])

    const { mutateAsync, isPending } = useMutationWithHandler(
        { mutationFn: handleEdit },
        {
            reactQueryKeys: [
                QUERY_KEYS.CUSTOMERS,
                QUERY_KEYS.CUSTOMERS_CONTACTS,
            ],
            successMessage: t('updated-success'),
            errorMessage: t('Something went wrong'),
        }
    )

    const contactId = watch('contact_id')

    useEffect(() => {
        setSearchParams(
            (prev) => {
                if (!contactId) prev.delete('contact_id')
                else prev.set('contact_id', String(contactId || ''))
                return prev
            },
            {
                replace: true,
            }
        )
    }, [contactId])

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div className="p-4 bg-white mt-2 rounded-md">
            <GreyMarket
                customerId={Number(id)}
                isGreyMarket={data?.is_grey_market || false}
            />

            {/* Contact Selection Header with Badge */}
            <div className="mb-3 flex items-center gap-4">
                <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-primary" />
                    <Typogaphy
                        size="small"
                        className="font-medium text-gray-700"
                    >
                        {t('contact_placeholder')}
                    </Typogaphy>
                </div>
                {totalContacts > 0 && (
                    <Badge
                        variant="outline-primary"
                        className="h-6 px-2.5 text-xs font-medium"
                    >
                        {totalContacts}{' '}
                        {totalContacts === 1 ? 'Contact' : 'Contacts'}
                    </Badge>
                )}
            </div>

            <div className="flex items-center gap-2">
                <div className="flex-1">
                    <ContactSelect
                        t={t}
                        name="contact_id"
                        control={control}
                        placeholder={t('contact_placeholder')}
                        customer_id={Number(id)}
                        afterFetch={(data) => {
                            setTotalContacts(data?.length || 0)
                            if (!data?.length) return
                            setValue('contact_id', data[0]?.value as number)
                        }}
                    />
                </div>
                {!!contactId && !isReadOnly && (
                    <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => {
                            setModal(
                                'CustomerModals/DeleteContactModal/index',
                                {
                                    customer_id: Number(id),
                                    contact_id: contactId,
                                    contact_name: data?.contact_name || '',
                                }
                            )
                        }}
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>
                )}
            </div>

            {showInfoContact && !!contactId && (
                <ViewEditContact
                    data={data!}
                    isEditing={isEditing}
                    setIsEditing={setIsEditing}
                    editFormControl={editFormControl}
                    onSave={mutateAsync}
                    isPending={isPending}
                    customerId={Number(id)}
                />
            )}
        </div>
    )
}

export default Contact
